import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time failures so one broken screen does not take the whole app down. The
 * message is deliberately specific about what to do next, and the detail is kept for the
 * console and for anyone who opens the panel.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Unhandled error in the interface", error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="wall" role="alert">
        <p className="wall-code">error</p>
        <h1>This screen stopped working</h1>
        <p>
          The page hit a problem it could not recover from on its own. Your progress is saved on
          the server, so reloading will not lose any work.
        </p>
        <div className="row">
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload the page
          </button>
          <button type="button" className="btn" onClick={this.reset}>
            Try this screen again
          </button>
        </div>
        <details>
          <summary className="dim">Technical detail</summary>
          <pre>{error.message}</pre>
        </details>
      </div>
    );
  }
}
